import { FetchError } from "@medusajs/js-sdk"
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"
import qs from "qs"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { sdk } from "../../lib/client"

// ─── Types ────────────────────────────────────────────────────────────

export interface MarketplaceNotification {
  id: string
  recipient_id: string
  recipient_type: "customer" | "admin"
  merchant_id: string | null
  type: string
  title: string
  content: string | null
  data: Record<string, any> | null
  action_url: string | null
  read_at: string | null
  seen_at: string | null
  created_at: string
  updated_at: string
}

export interface MarketplaceNotificationListResponse {
  notifications: MarketplaceNotification[]
  count: number
  limit: number
  offset: number
}

export interface MarketplaceNotificationUnreadCountResponse {
  count: number
}

export interface MarketplaceNotificationReadResponse {
  success: boolean
  count: number
}

// ─── Query Keys ───────────────────────────────────────────────────────

const MARKETPLACE_NOTIFICATION_QUERY_KEY = "marketplace-notifications" as const
export const marketplaceNotificationQueryKeys = queryKeysFactory(
  MARKETPLACE_NOTIFICATION_QUERY_KEY
)

// ─── Query Hooks ──────────────────────────────────────────────────────

/**
 * List marketplace notifications (paginated, scoped by admin role).
 * GET /admin/marketplace-notifications
 */
export const useMarketplaceNotifications = (
  query?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<
      MarketplaceNotificationListResponse,
      FetchError,
      MarketplaceNotificationListResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const filterQuery = qs.stringify(query ?? {}, {
    arrayFormat: "indices",
    encodeValuesOnly: true,
    skipNulls: true,
  })

  const fetchNotifications = async () =>
    sdk.client.fetch<MarketplaceNotificationListResponse>(
      `/admin/marketplace-notifications${filterQuery ? `?${filterQuery}` : ""}`,
      { method: "GET" }
    )

  const { data, ...rest } = useQuery({
    queryKey: marketplaceNotificationQueryKeys.list(query),
    queryFn: fetchNotifications,
    ...options,
  })

  return { ...data, ...rest }
}

/**
 * Get unread notification count (server-side).
 * GET /admin/marketplace-notifications/unread-count
 */
export const useMarketplaceNotificationUnreadCount = (
  options?: Omit<
    UseQueryOptions<
      MarketplaceNotificationUnreadCountResponse,
      FetchError,
      MarketplaceNotificationUnreadCountResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const fetchUnreadCount = async () =>
    sdk.client.fetch<MarketplaceNotificationUnreadCountResponse>(
      "/admin/marketplace-notifications/unread-count",
      { method: "GET" }
    )

  const { data, ...rest } = useQuery({
    queryKey: [...marketplaceNotificationQueryKeys.all, "unread-count"],
    queryFn: fetchUnreadCount,
    refetchInterval: 15_000, // 15s fallback polling
    ...options,
  })

  return { count: data?.count ?? 0, ...rest }
}

// ─── Mutation Hooks ───────────────────────────────────────────────────

/**
 * Mark specific notifications as read.
 * POST /admin/marketplace-notifications/read
 */
export const useMarkNotificationsAsRead = (
  options?: UseMutationOptions<
    MarketplaceNotificationReadResponse,
    FetchError,
    { ids: string[] }
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { ids: string[] }) =>
      sdk.client.fetch<MarketplaceNotificationReadResponse>(
        "/admin/marketplace-notifications/read",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceNotificationQueryKeys.all,
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

/**
 * Mark all notifications as read.
 * POST /admin/marketplace-notifications (mark-all)
 */
export const useMarkAllNotificationsAsRead = (
  options?: UseMutationOptions<
    MarketplaceNotificationReadResponse,
    FetchError,
    void
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () =>
      sdk.client.fetch<MarketplaceNotificationReadResponse>(
        "/admin/marketplace-notifications",
        { method: "POST" }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: marketplaceNotificationQueryKeys.all,
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}
